import React, { useEffect, useMemo, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'hooks';
import { useNavigate, useParams } from 'react-router-dom';

import { getDefaults } from '~appointments/utils/defaults';
import classNames from 'classnames';

import { AuthTags } from '~constants/authTags';
import { CASE_MANAGEMENT_NOTE_TYPE_ID } from '~constants/common';
import { getMissingContent } from '~constants/notFoundMap';

import { useAllSearchParams } from '~libs/searchParams';

import ConfirmDialog from '~components/ConfirmDialog';
import ContainerLoading from '~components/ContainerLoading';
import ErrorResponse from '~components/ErrorResponse';
import { FieldProps } from '~components/form-fields';
import PageNotFound from '~components/PageNotFound';
import TabContainer from '~components/TabContainer';
import AppointmentBanner from '~appointments/components/AppointmentBanner';
import AppointmentDeclined from '~appointments/components/AppointmentDeclined';
import AppointmentForm from '~appointments/components/AppointmentForm';
import GroupAppointmentSummary from '~appointments/components/group/GroupAppointmentSummary';
import GroupAppointmentAttendees from '~appointments/components/group/GroupAppointmentAttendees';
import GroupAppointmentSettings from '~appointments/components/group/GroupAppointmentSettings';

import { get } from '~appointments/forms';

import { getAll as getActivities } from '~appointments/actions/activities';
import { addGroupChildren, create, getOne, getSignature, update } from '~appointments/actions/appointments';
import { getAll as getCareQualificationLevels } from '~appointments/actions/careQualificationLevels';
import { getAll as getLocations } from '~appointments/actions/locations';
import { getAll as getRepeatFrequencies } from '~appointments/actions/repeatFrequencies';
import { getAll as getServiceTypes } from '~appointments/actions/serviceTypes';
import { getAll as getAppointmentStatuses } from '~appointments/actions/statuses';
import { getAvailability, getAll as getCareWorkers, getOne as getOneCareWorker } from '~care-workers/actions';
import { getAll as getEquipments } from '~lookups/actions/equipments';
import { getAll as getLeaveCodes } from '~lookups/actions/leaveCodes';
import { getAll as getMedications } from '~lookups/actions/medications';
import { getAll as getPaymentTypes } from '~lookups/actions/paymentTypes';
import { getAll as getTaskClassifications } from '~lookups/actions/taskClassifications';
import { getAll as getTaskTypes } from '~lookups/actions/taskTypes';
import { getAll as getClients, getOne as getOneClient } from '~main/actions/clients';
import { getAll as getAllFormDefaults } from '~main/actions/formDefaults';
import { generateSession, getFeatureFlag, setTitle } from '~main/actions/login';

import {
  clearAppointment,
  clearChildAppointment,
  clearCopyAppointment,
  clearError,
} from '~appointments/reducers/appointments';
import { clearQualifications } from '~appointments/reducers/careQualificationLevels';
import { clearUserAppointments } from '~appointments/reducers/userAppointments';

import {
  selectAppointmentLoading,
  selectAppointmentRowsLoading,
  selectChildAppointment,
  selectCopyAppointment,
  selectCurrentAppointment,
  selectErrorResponse,
} from '~appointments/selectors/appointments';
import { selectAppointmentServiceTypes } from '~appointments/selectors/serviceTypes';
import { selectCurrentUser } from '~care-workers/selectors/users';
import { selectCurrentClient } from '~main/selectors/clients/clients';
import { selectFormDefaults } from '~main/selectors/formDefaults';
import {
  selectAuthenticatedUser,
  selectCompanyDefaults,
  selectCompanySettings,
  selectUserTags,
} from '~main/selectors/login';

interface ComponentProps {
  isNew?: boolean;
  isTransport?: boolean;
  forceRepeat?: boolean;
  isSupportCoordination?: boolean;
  isCaseManagement?: boolean;
  isNewGroupParent?: boolean;
  isNewGroupChild?: boolean;
  participantType?: 'user' | 'client';
}

const Appointment: React.FC<ComponentProps> = ({
  isNew = false,
  isTransport = false,
  forceRepeat = false,
  isSupportCoordination = false,
  isCaseManagement: forceCaseManagement = false,
  isNewGroupParent = false,
  isNewGroupChild = false,
  participantType = null,
}) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const { id, group_id } = useParams();
  const searchParamValues = useAllSearchParams();
  const REDIRECT_URL = process.env.REACT_APP_TPONE_URL;

  const [activeTab, setActiveTab] = useState<number>(0);

  // Validation fields and confirmation messages to display
  const [changedValues, setChangedValues]: [any, any] = useState({});
  const [confirmMessages, setConfirmMessages]: [string[], any] = useState([]);
  const [confirmOpen, setConfirmOpen]: [boolean, any] = useState(false);
  const [crmNoteOpen, setCrmNoteOpen]: [boolean, any] = useState(false);
  const [pendingSaveValues, setPendingSaveValues]: [any, any] = useState(null);
  const [isRedirecting, setIsRedirecting]: [boolean, any] = useState(false);

  const [fields, setFields]: [FieldProps, any] = useState({});

  const appointment = useAppSelector(selectCurrentAppointment);
  const copyValues = useAppSelector(selectCopyAppointment);

  const childValues = useAppSelector(selectChildAppointment);
  const selectAppointmentsRowLoading = useAppSelector(selectAppointmentRowsLoading);
  const selectAppointmentsLoading = useAppSelector(selectAppointmentLoading);
  const serviceTypes = useAppSelector(selectAppointmentServiceTypes);
  const companyDefaults = useAppSelector(selectCompanyDefaults);
  const companyFormDefaults = useAppSelector(selectFormDefaults);
  const defaultClient = useAppSelector(selectCurrentClient);

  const companySettings = useAppSelector(selectCompanySettings);
  const defaultUser = useAppSelector(selectCurrentUser);
  const tags = useAppSelector(selectUserTags);
  const authenticatedUser = useAppSelector(selectAuthenticatedUser);

  const isLoading = isNew
    ? selectAppointmentsLoading === 'pending'
    : selectAppointmentsRowLoading !== 'fulfilled' || selectAppointmentsLoading === 'pending';
  const isRepeat = appointment?.repeat ?? forceRepeat;
  const isDeclined = appointment?.declined;
  const isPendingDeclined = isDeclined && appointment?.chargeable_cancellation_actioned === false;
  const canViewActivities = tags.includes(AuthTags.APPOINTMENTS.ACTIVITIES.VIEW);
  const errorResponse = useAppSelector(selectErrorResponse);

  const isExistingGroupParent = appointment?.group_session_id === 1;
  const isExistingGroupChild = appointment?.group_session_id > 1;
  const isNewGroup = isNewGroupParent || isNewGroupChild;
  const isCaseManagement = appointment?.case_management ?? forceCaseManagement;
  const isGroup = isNewGroup || isExistingGroupParent || isExistingGroupChild;
  const isGroupParent = isNewGroupParent || isExistingGroupParent;
  const { SYSTEM_SupportCoordinationServiceTypeID } = companySettings;
  const serviceTypeParams =
    SYSTEM_SupportCoordinationServiceTypeID &&
    (Array.isArray(SYSTEM_SupportCoordinationServiceTypeID) || !isNaN(Number(SYSTEM_SupportCoordinationServiceTypeID)))
      ? SYSTEM_SupportCoordinationServiceTypeID
      : null;

  const appointmentType = (() => {
    switch (true) {
      case isCaseManagement:
        return 'case-management';
      case isSupportCoordination:
        return 'support-coordination';
      case isGroup:
        return 'group';
      default:
        return 'single';
    }
  })();

  const supportCoordinationId = companyDefaults?.support_coordination_service_id;
  const formDefaultDependencies = { companySettings };
  const formDefaults = getDefaults(appointmentType, formDefaultDependencies, companyFormDefaults);

  useEffect(() => {
    // Get appointment by ID on mount if new
    loadCommon();
    if (isNew) loadNew();
    if (!isNew) loadExisting();
    // clear any old errors
    dispatch(clearError());

    return () => {
      dispatch(clearQualifications([]));
      dispatch(clearAppointment());
      dispatch(clearUserAppointments());

      if (isNew) {
        dispatch(clearCopyAppointment());
        dispatch(clearChildAppointment());
      }
    };
  }, []);

  useEffect(() => {
    if (isSupportCoordination && !tags.includes(AuthTags.APPOINTMENTS.SUPPORTCOORDINATION.UPDATE)) {
      navigate('/access-denied?type=1');
    }
    if (isCaseManagement && !tags.includes(AuthTags.APPOINTMENTS.CASEMANAGEMENT.UPDATE)) {
      navigate('/access-denied?type=1');
    }
  }, [tags]);

  useEffect(() => {
    if (appointment && !isNew) {
      const redirect = async () => {
        if (appointment?.is_group_session === true) {
          const { payload } = await dispatch(getFeatureFlag({ flag: 'feature-group-appointments' }));
          const { data: isEnabled } = payload;

          if (!isEnabled) {
            setIsRedirecting(true);
            window.location.replace(`${REDIRECT_URL}/appointment-edit.asp?eid=${id}`);
          }
        }
      };
      redirect();

      setTitle(`Appt #${appointment?.reference_number ?? ''}, ${appointment?.client?.full_name ?? ''}`);
      dispatch(getClients(isGroup ?? { id: appointment.client_id }));

      if (appointment.user_id) {
        dispatch(
          getAvailability({
            id: appointment.user_id,
            appointment_date: appointment.date,
          }),
        );
      }

      if (appointment.photos?.length > 0) {
        dispatch(generateSession());
      }
    }
  }, [appointment, copyValues]);

  const loadCommon = () => {
    dispatch(getRepeatFrequencies());
    dispatch(getAppointmentStatuses());
    dispatch(getServiceTypes(isSupportCoordination ? { id: serviceTypeParams } : undefined));
    dispatch(getCareQualificationLevels({ appt_match: true }));
    dispatch(getLeaveCodes());
    dispatch(getPaymentTypes());
    dispatch(getTaskClassifications());
    dispatch(getTaskTypes({ disabled: false, sort: ['classification', 'name'] }));
    dispatch(getMedications({ disabled: false, sort: ['name'] }));
    dispatch(getEquipments({ disabled: false, sort: ['name'] }));
    dispatch(getLocations());
  };

  const loadExisting = () => {
    dispatch(getOne({ id, include_repeat_next_date: true }));
    dispatch(getSignature({ id }));
    dispatch(getActivities({ parent_id: id, sort: ['id', 'date_time'] }));
    dispatch(getCareWorkers());
  };

  const loadNew = () => {
    setTitle('New Appointment');
    dispatch(getServiceTypes(isSupportCoordination ? { id: serviceTypeParams } : undefined));
    dispatch(getCareWorkers());
    dispatch(getClients());
    dispatch(getAllFormDefaults({ feature_tag: 'appointments' }));

    const { copy_id, parent_id } = searchParamValues;
    if (copy_id) {
      dispatch(getOne({ id: copy_id, isTemplate: true, include_repeat_next_date: true }));
    } else if (parent_id) {
      dispatch(getOne({ id: parent_id, isParent: true, include_repeat_next_date: true }));
    } else if (participantType) {
      dispatch(getOne({ id: group_id, isChild: true, include_repeat_next_date: true }));
      dispatch(getOneCareWorker({ id: companySettings.SYSTEM_2022_GROUP_SESSION_USER_ID }));
      dispatch(getOneClient({ id: companySettings.SYSTEM_2022_GROUP_SESSION_CLIENT_ID }));
    }
  };

  const onConfirm = (confirmed: boolean, values: any) => {
    setConfirmOpen(false);
    setConfirmMessages([]);

    if (confirmed) save(values);
  };

  const canSave = () => {
    if (appointment) {
      const { exported_payroll, exported_invoices } = appointment;
      if (!authenticatedUser?.accounts_access_enabled && (exported_payroll || exported_invoices)) {
        return false;
      }
    }
    return true;
  };

  const onSave = async (values: any, originalValues: any) => {
    if (validateConfirm(values, originalValues)) {
      save(values);
    } else {
      setChangedValues(values);
      setConfirmOpen(true);
    }
  };

  const save = async (values: any) => {
    if (isNew) {
      let group_session_id = undefined;
      if (isNewGroup) {
        group_session_id = isNewGroupParent ? 1 : group_id;
      }

      // childValues contains parent_id (which doesn't have a form field), so must be retained when creating a single appt from parent. Legacy garbage (╯°□°）╯︵ ┻━┻
      const finalValues = childValues
        ? { ...childValues, ...values }
        : {
            ...values,
            repeat: isRepeat,
            support_coordination: isSupportCoordination,
            case_management: isCaseManagement,
            group_session_id,
          };

      const { payload } = await dispatch(create(finalValues));
      if (payload?.data?.id) navigate(`/appointments/${payload.data.id}`);
    } else {
      // For case management updates, prompt the user about CRM note before saving
      if (isCaseManagement && !('add_crm_note' in values)) {
        setPendingSaveValues(values);
        setCrmNoteOpen(true);
        return;
      }

      const { clients, users, ...rest } = values;
      if (isGroup && (users !== undefined || clients !== undefined)) {
        dispatch(addGroupChildren({ id, clients, users }));
      }
      const { payload } = await dispatch(
        update({
          id,
          ...rest,
        }),
      );
      if (payload?.success) loadExisting();
      // Scroll to top of container on success and failure.
      window.scrollTo(0, 0);
    }
  };

  const onCrmNoteConfirm = (addNote: boolean) => {
    setCrmNoteOpen(false);
    if (pendingSaveValues) {
      save({ ...pendingSaveValues, add_crm_note: addNote });
    }
  };

  const validateConfirm = (values: any, originalValues: any) => {
    if (
      values.user_id === companySettings.SYSTEM_2022_GROUP_SESSION_USER_ID ||
      values.user_id === companyDefaults?.support_coordination_service_id
    ) {
      return true;
    }

    if (!isNew && isCaseManagement && values.notes !== originalValues.notes) {
      setConfirmMessages((prevMessages: string[]) => [
        ...prevMessages,
        'Your Case Management notes will be saved as a new entry. All previous notes will remain unchanged to maintain a complete record.',
      ]);
      return false;
    }

    const definitions = fieldDefinitions.filter(({ show }) => {
      if (typeof show === 'function') return show(values);
      if (show === undefined) return true;
      return show;
    });

    let valid = true;
    definitions.forEach(({ key }) => {
      const definition = fieldDefinitions.find(({ key: fieldKey }) => fieldKey === key);

      if (definition?.validateConfirm) {
        const result = definition.validateConfirm(values, originalValues);
        if (!result.valid) {
          valid = false;
          if (result.message) {
            setConfirmMessages((prevMessages: string[]) => [...prevMessages, result.message]);
          }
        }
      }
    });

    return valid;
  };

  // Determine field definitions to use
  useEffect(() => {
    if (serviceTypes.length > 0) {
      const updatedFields = Object.entries(
        get({
          isNew,
          isRepeat,
          isSupportCoordination,
          isCaseManagement,
          isNewGroup,
          isExistingGroupParent,
          participantType,
          ...appointment,
        }),
      ).map(([key, props]) => ({
        key,
        disabled: !canSave(),
        ...props,
      }));

      setFields(updatedFields);
    }
  }, [serviceTypes, isSupportCoordination, isCaseManagement, isGroup]);

  useEffect(() => {
    const newFields = get({
      isNew,
      isRepeat,
      isSupportCoordination,
      isCaseManagement,
      isNewGroup,
      isExistingGroupParent,
      participantType,
      ...appointment,
    });
    setFields(newFields);
  }, [isNew, isRepeat, appointment, isSupportCoordination, isCaseManagement, isGroup, participantType]);

  const fieldDefinitions = Object.entries(fields).map(([key, props]) => ({
    key,
    disabled: !canSave(),
    ...props,
  }));

  const defaultValues = useMemo(() => {
    const values: { transport: boolean; note_type_id?: number } = {
      transport: isTransport,
    };

    if (isCaseManagement) {
      values.note_type_id = CASE_MANAGEMENT_NOTE_TYPE_ID;
    }

    return values;
  }, [isTransport, isCaseManagement]);

  const overrideValues = useMemo(() => {
    const sanitizedValues: { [key: string]: any } = { ...searchParamValues };

    // Convert *_id values to numbers
    for (const key in sanitizedValues) {
      if (key.endsWith('_id')) {
        const value = sanitizedValues[key];
        const numberValue = Number(value);
        if (!isNaN(numberValue)) {
          sanitizedValues[key] = numberValue;
        } else {
          console.error('Error while converting search param value:', key, value);
        }
      }
    }

    let overrideSearchValues = { ...sanitizedValues };

    // Role-specific overrides
    switch (true) {
      case isSupportCoordination:
        overrideSearchValues = {
          ...formDefaults,
          service_type_id: supportCoordinationId,
          support_coordination: true,
          user_id: authenticatedUser?.id,
          ...overrideSearchValues,
        };
        break;

      case isCaseManagement:
        overrideSearchValues = {
          ...formDefaults,
          case_management: true,
          user_id: isNew ? authenticatedUser?.id : undefined,
          ...overrideSearchValues,
        };
        break;
      default:
        break;
    }

    // Merge child values if parent_id exists
    if (overrideSearchValues.parent_id) {
      return {
        ...childValues,
        ...overrideSearchValues,
      };
    }

    // Final override for new group or copied values
    return {
      ...((isNew || participantType) && copyValues),
      ...overrideSearchValues,

      ...(isNewGroupParent && {
        client_id: companySettings?.SYSTEM_2022_GROUP_SESSION_CLIENT_ID,
        user_id: companySettings?.SYSTEM_2022_GROUP_SESSION_USER_ID,
        user: defaultUser,
        client: defaultClient,
      }),

      ...(isNewGroupChild && {
        client_id: participantType === 'user' && companySettings?.SYSTEM_2022_GROUP_SESSION_CLIENT_ID,
        user_id: participantType === 'client' && companySettings?.SYSTEM_2022_GROUP_SESSION_USER_ID,
        user: defaultUser,
        client: defaultClient,
      }),
      repeat: isRepeat,
    };
  }, [
    isNew,
    copyValues,
    isRepeat,
    childValues,
    searchParamValues,
    isSupportCoordination,
    isCaseManagement,
    companySettings,
    participantType,
    isGroup,
    isNewGroupParent,
    defaultUser,
    defaultClient,
    supportCoordinationId,
    formDefaults,
    authenticatedUser,
  ]);

  const buttonProps = {
    authTag: AuthTags.APPOINTMENTS.UPDATE,
    color: 'success',
    className: 'ms-4 me-4 ps-4 pe-4',
    children: `${isNew ? 'Add' : 'Update'} Appointment`,
    ['data-cy']: 'appointment-submit-button',
  };

  const groupTabs =
    appointment && isExistingGroupParent && !isNew
      ? [
          {
            id: 'group-summary',
            title: 'Summary',
            content: (
              <GroupAppointmentSummary
                appointment={appointment}
                onEditSettings={() => setActiveTab(2)}
                onQuickAdd={(type, item) => {
                  const nextUsers = type === 'users' ? [...(appointment.users ?? []), item] : appointment.users;
                  const nextClients = type === 'clients' ? [...(appointment.clients ?? []), item] : appointment.clients;
                  onSave({ users: nextUsers, clients: nextClients }, appointment);
                }}
              />
            ),
          },
          {
            id: 'group-attendees',
            title: 'Attendees',
            content: (
              <GroupAppointmentAttendees
                appointment={appointment}
                id={id ?? ''}
                isRepeat={isRepeat}
                onSave={(users: any[], clients: any[]) => onSave({ users, clients }, appointment)}
              />
            ),
          },
          {
            id: 'group-settings',
            title: 'Settings',
            content: (
              <GroupAppointmentSettings
                appointment={appointment}
                fieldDefinitions={fieldDefinitions}
                defaultValues={defaultValues}
                overrideValues={overrideValues}
                canSave={canSave}
                onSave={onSave}
                isRepeat={isRepeat}
                authTag={AuthTags.APPOINTMENTS.UPDATE}
                confirmButtonProps={buttonProps}
              />
            ),
          },
        ]
      : null;

  const getBannerProps = () => {
    const isRepeat = appointment?.repeat ?? forceRepeat;
    const isGroup = isNewGroup || isExistingGroupParent || isExistingGroupChild;

    // Header class name determination
    const headerClassName = classNames({
      warning: isRepeat,
      info: !isRepeat && !isSupportCoordination && !isCaseManagement && !isExistingGroupParent,
      'support-coordination': isSupportCoordination,
      'case-management': isCaseManagement,
      'group-appointment': isGroup,
      'text-white': isRepeat || isSupportCoordination || isCaseManagement || isGroup,
    });

    // Header text determination
    let headerText;
    switch (true) {
      case isSupportCoordination:
        headerText = `Support Coordination Entry - ${isNew ? 'New' : `Ref #${appointment?.reference_number}`}`;
        break;
      case isCaseManagement:
        headerText = `Case Management Entry - ${isNew ? 'New' : `Ref #${appointment?.reference_number}`}`;
        break;
      case isGroup:
        headerText = `Group Appointment Details - ${isNew ? 'New' : `Ref #${appointment?.reference_number}`}`;
        break;
      case !appointment:
        headerText = `Appointment Details - ${isNew ? 'New' : `Not Found`}`;
        break;
      default:
        headerText = `Appointment Details - Ref #${appointment?.reference_number}`;
    }

    // Data-cy attribute determination
    let dataCy;
    switch (true) {
      case isSupportCoordination:
        dataCy = 'support-coordination-header';
        break;
      case isCaseManagement:
        dataCy = 'case-management-header';
        break;
      case isGroup:
        dataCy = 'group-header';
        break;
      case isRepeat:
        dataCy = 'repeat-appointments-header';
        break;
      default:
        dataCy = 'appointment-header';
    }

    // Return to determination
    let returnTo;
    switch (true) {
      case (isNewGroup || isExistingGroupParent) && !isRepeat:
        returnTo = 'groups';
        break;
      case isRepeat:
        returnTo = 'repeats';
        break;
      default:
        returnTo = 'appointments';
    }

    // Icon color determination
    let iconColor;
    if (isSupportCoordination || isCaseManagement || isRepeat) {
      iconColor = 'white';
    }

    return {
      headerClassName,
      headerText,
      dataCy,
      iconColor,
      formDefaults: companyFormDefaults,
      showRepeatLabel: isRepeat,
      showTransportLabel: isTransport,
      isNew,
      onBackNavigatesTo: returnTo,
      isCaseManagement,
    };
  };

  return (
    <div className="p-2">
      <ConfirmDialog
        isOpen={confirmOpen}
        messages={confirmMessages}
        onClose={(confirm) => onConfirm(confirm, changedValues)}
      />
      <ConfirmDialog
        isOpen={crmNoteOpen}
        messages="Do you want to add a new note into the CRM from this appointment?"
        onClose={onCrmNoteConfirm}
        useList={false}
      />
      <AppointmentBanner {...getBannerProps()} />
      <ErrorResponse message={errorResponse} dataCy="appointments-errorResponse" />
      {isPendingDeclined && (
        <AppointmentDeclined
          appointment={appointment}
          onAcknowledge={() => save({ chargeable_cancellation_actioned: true })}
        />
      )}
      <ContainerLoading loading={isLoading || isRedirecting} bgColor="white" color="black" isTransparent={true}>
        {!isLoading && !isRedirecting && (
          <>
            {appointment && isExistingGroupParent && !isNew && groupTabs && (
              <TabContainer
                tabs={groupTabs}
                setPageTitle={false}
                activeTab={activeTab}
                onTabChange={setActiveTab}
              />
            )}
            {(!appointment || !isExistingGroupParent || isNew) && (
              <>
                {appointment || isNew ? (
                  <AppointmentForm
                    authTag={AuthTags.APPOINTMENTS.UPDATE}
                    fields={fieldDefinitions}
                    defaultValues={defaultValues}
                    overrideValues={overrideValues}
                    canSave={canSave}
                    save={onSave}
                    isNew={isNew}
                    isGroup={isGroupParent}
                    confirmButtonProps={buttonProps}
                    showActivityLogs={!isNew && canViewActivities}
                    showFullNotes={isCaseManagement}
                  />
                ) : (
                  <PageNotFound content={getMissingContent('appointment')} />
                )}
              </>
            )}
          </>
        )}
      </ContainerLoading>
    </div>
  );
};

export default Appointment;
